import React, { useMemo } from 'react';
import { FieldConfig, MappingValue, DropdownOption, SourceType } from './types';
import Dropdown from 'Components/DesignSystem/Dropdown/Dropdown';

type MappingRowProps = {
    id?: string;
    fieldConfig: FieldConfig;
    mapping: MappingValue | undefined;
    isEditMode: boolean;
    isEvenRow: boolean;
    fieldOptions: DropdownOption[];
    onSourceChange: (fieldKey: string, source: string) => void;
    onValueChange: (fieldKey: string, value: string) => void;
    hasSourceError?: boolean;
    hasValueError?: boolean;
};

export const MappingRow = ({
    id,
    fieldConfig,
    mapping,
    isEditMode,
    isEvenRow,
    fieldOptions,
    onSourceChange,
    onValueChange,
    hasSourceError = false,
    hasValueError = false,
}: MappingRowProps) => {
    const { key: fieldKey, label: fieldLabel, isMandatory, allowedSources, fixedValue } = fieldConfig;

    const rowId = id ?? `mapping-row-${fieldKey}`;

    const sourceType = (mapping?.source ?? '') as SourceType | '';

    const sourceOptions = useMemo<DropdownOption[]>(
        () => allowedSources.map((source) => ({ label: source, value: source })),
        [allowedSources]
    );

    const displayValue = useMemo(() => {
        if (sourceType === 'Fixed Value') {
            return fixedValue ?? '';
        }
        return mapping?.value ?? '';
    }, [sourceType, fixedValue, mapping?.value]);

    const handleSourceChange = (value: string | number) => {
        onSourceChange(fieldKey, String(value));
    };

    const handleValueDropdownChange = (value: string | number) => {
        onValueChange(fieldKey, String(value));
    };

    const handleValueInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onValueChange(fieldKey, e.target.value);
    };

    const rowPadding = 'tw-py-2 tw-ps-4';
    const truncatedCell = 'tw-truncate tw-max-w-0 tw-overflow-hidden tw-whitespace-nowrap';
    const bgColor = isEvenRow ? 'tw-bg-white' : 'tw-bg-gray-100';
    const textStyle = 'tw-text-blue-950 tw-text-sm tw-font-normal';

    const getInputBorderStyle = (hasError: boolean) => {
        return hasError ? 'tw-border-red-500' : 'tw-border-gray-300';
    };

    const renderValueCell = () => {
        switch (sourceType) {
            case 'Field':
                return (
                    <div data-testid={`${rowId}-value-dropdown-container`}>
                        <Dropdown
                            options={fieldOptions}
                            value={mapping?.value ?? ''}
                            onChange={handleValueDropdownChange}
                            placeholder="Select Field"
                            label=""
                            usePortal
                            error={hasValueError}
                        />
                    </div>
                );

            case 'Attribute':
                return (
                    <input
                        type="text"
                        className={`tw-w-full tw-border ${getInputBorderStyle(hasValueError)} tw-rounded tw-px-2 tw-py-1.5 tw-text-sm tw-text-[#172B4D] focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500 focus:tw-border-blue-500`}
                        value={mapping?.value ?? ''}
                        placeholder="Enter Value"
                        onChange={handleValueInputChange}
                        data-testid={`${rowId}-value-input`}
                    />
                );

            case 'Fixed Value':
                return (
                    <input
                        type="text"
                        className="tw-w-full tw-border-0 tw-rounded tw-px-2 tw-py-1.5 tw-text-sm tw-text-[#172B4D] tw-bg-[#DFE1E6] tw-cursor-not-allowed"
                        value={fixedValue ?? ''}
                        disabled
                        readOnly
                        data-testid={`${rowId}-value-display`}
                    />
                );

            default:
                return (
                    <input
                        type="text"
                        className={`tw-w-full tw-border ${getInputBorderStyle(hasValueError)} tw-rounded tw-px-2 tw-py-1.5 tw-text-sm tw-text-[#172B4D] tw-bg-gray-50 tw-cursor-not-allowed`}
                        value=""
                        placeholder="Select a source first"
                        disabled
                        data-testid={`${rowId}-value-placeholder`}
                    />
                );
        }
    };

    const renderViewMode = () => (
        <>
            <td
                className={`${rowPadding} ${truncatedCell} ${bgColor} ${textStyle}`}
                data-testid={`${rowId}-source`}
            >
                {mapping?.source ?? '-'}
            </td>
            <td
                className={`${rowPadding} ${truncatedCell} ${bgColor} ${textStyle}`}
                title={displayValue}
                data-testid={`${rowId}-value`}
            >
                {displayValue || '-'}
            </td>
        </>
    );

    const renderEditMode = () => (
        <>
            <td
                className={`tw-ps-4 ${bgColor} ${textStyle}`}
                data-testid={`${rowId}-source-cell`}
            >
                <div data-testid={`${rowId}-source-dropdown-container`}>
                    <Dropdown
                        options={sourceOptions}
                        value={sourceType}
                        onChange={handleSourceChange}
                        placeholder="Select source"
                        label=""
                        usePortal
                        error={hasSourceError}
                    />
                </div>
            </td>
            <td
                className={`${rowPadding} ${bgColor} ${textStyle} tw-pe-4`}
                data-testid={`${rowId}-value-cell`}
            >
                {renderValueCell()}
            </td>
        </>
    );

    return (
        <tr
            className="hover:tw-bg-gray-50"
            data-testid={rowId}
        >
            <td
                className={`${rowPadding} ${truncatedCell} ${bgColor} tw-pl-4 tw-text-blue-950 tw-text-sm tw-font-normal`}
                title={fieldLabel}
                data-testid={`${rowId}-label`}
            >
                {fieldLabel}
                {!isMandatory && <span className="tw-text-[#6B778C] tw-ml-1">(Optional)</span>}
            </td>
            {isEditMode ? renderEditMode() : renderViewMode()}
        </tr>
    );
};